/**
 * Iter 25 (D2) — Inline ROI calculator on /pricing.
 *
 * Visitors land on /pricing, see $29 + $9, and the immediate question is
 * "is that worth it for me?". This calculator answers that with their
 * own numbers — three sliders feed live monthly-savings math.
 *
 * Tone is calm and transparent — the assumptions are exposed via a
 * "How we calculated this" disclosure, not hidden behind hype.
 *
 * Outputs are computed locally (no backend call) so dragging a slider
 * is instant. The "Start saving today" CTA links to `/signup?promo=FOUNDER19`
 * so the calculator's headline number ties to the $19 price the visitor
 * sees underneath it.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Slider } from "@/components/ui/slider";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ArrowRight, ChevronDown, Info } from "lucide-react";

const SYMPLEE_AUTOMATION_RATIO = 0.6; // ~60% of manual hours moved off the team
const SYMPLEE_MONTHLY_COST = 19;       // FOUNDER19 base seat
const SYMPLEE_MONTHLY_COST_FULL = 29;  // sticker price (used in "how we calculate")
const WEEKS_PER_MONTH = 4;

function formatUSD(n) {
  if (!Number.isFinite(n)) return "$0";
  return `$${Math.round(n).toLocaleString("en-US")}`;
}

export function ROICalculator() {
  const [leads, setLeads] = useState(100);            // 10..5000
  const [hoursPerWeek, setHoursPerWeek] = useState(8); // 1..40
  const [hourlyCost, setHourlyCost] = useState(40);   // 20..150

  const computed = useMemo(() => {
    const hoursSavedPerMonth = hoursPerWeek * WEEKS_PER_MONTH * SYMPLEE_AUTOMATION_RATIO;
    const monthlyLaborSavings = hoursSavedPerMonth * hourlyCost;
    const netMonthlyValue = Math.max(0, monthlyLaborSavings - SYMPLEE_MONTHLY_COST);
    return { hoursSavedPerMonth, monthlyLaborSavings, netMonthlyValue };
  }, [hoursPerWeek, hourlyCost]);

  return (
    <section
      className="rounded-3xl border border-white/10 bg-white/[0.02] p-6 sm:p-8"
      data-testid="roi-calculator"
      aria-labelledby="roi-heading"
    >
      <div className="flex flex-col gap-1">
        <span className="text-eyebrow text-emerald-400">Worth it?</span>
        <h2
          id="roi-heading"
          className="font-display text-3xl font-light leading-tight tracking-tight sm:text-4xl"
          data-testid="roi-heading"
        >
          Run the math on <span className="brand-text">your own numbers</span>.
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-white/65">
          Three sliders. No sign-up. Honest assumptions exposed below.
        </p>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1.1fr_1fr]">
        {/* Sliders */}
        <div className="space-y-5">
          <SliderRow
            label="Leads you handle / month"
            value={leads}
            min={10}
            max={5000}
            step={10}
            format={(v) => v.toLocaleString("en-US")}
            onChange={setLeads}
            testid="roi-slider-leads"
          />
          <SliderRow
            label="Hours / week on manual outreach + content"
            value={hoursPerWeek}
            min={1}
            max={40}
            step={1}
            format={(v) => `${v} hrs`}
            onChange={setHoursPerWeek}
            testid="roi-slider-hours"
          />
          <SliderRow
            label="Average team hourly cost"
            value={hourlyCost}
            min={20}
            max={150}
            step={5}
            format={(v) => `$${v}/hr`}
            onChange={setHourlyCost}
            testid="roi-slider-cost"
          />
        </div>

        {/* Output card */}
        <div className="flex flex-col justify-between gap-4 rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.06] p-5">
          <div>
            <div className="text-eyebrow text-emerald-300/90">Estimated net value</div>
            <div
              className="font-display mt-2 text-5xl font-light tracking-tight text-slate-900 dark:text-white"
              data-testid="roi-output-net"
            >
              {formatUSD(computed.netMonthlyValue)}
              <span className="ml-2 text-base font-normal text-slate-600 dark:text-white/55">
                saved / month
              </span>
            </div>
          </div>

          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-white/45">
                Hours back / month
              </dt>
              <dd className="mt-1 font-medium text-slate-900 dark:text-white" data-testid="roi-output-hours">
                {Math.round(computed.hoursSavedPerMonth)} hrs
              </dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-white/45">
                Labor savings
              </dt>
              <dd className="mt-1 font-medium text-slate-900 dark:text-white" data-testid="roi-output-labor">
                {formatUSD(computed.monthlyLaborSavings)}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-white/45">
                Symplee cost
              </dt>
              <dd className="mt-1 font-medium text-slate-900 dark:text-white">
                ${SYMPLEE_MONTHLY_COST}/mo
                <span className="ml-1 text-[10px] uppercase tracking-wider text-emerald-400">
                  with FOUNDER19
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-white/45">
                Leads / month
              </dt>
              <dd className="mt-1 font-medium text-slate-900 dark:text-white">
                {leads.toLocaleString("en-US")}
              </dd>
            </div>
          </dl>

          <Link
            to="/signup?promo=FOUNDER19"
            data-testid="roi-cta"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-700"
          >
            Start saving today <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
          </Link>
        </div>
      </div>

      {/* Transparent assumptions */}
      <Collapsible className="mt-5">
        <CollapsibleTrigger
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 dark:text-white/55 dark:hover:text-white"
          data-testid="roi-assumptions-toggle"
        >
          <Info className="h-3.5 w-3.5" /> How we calculated this
          <ChevronDown className="h-3.5 w-3.5 transition data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
        <CollapsibleContent
          className="mt-3 rounded-xl border border-white/10 bg-white/[0.02] p-4 text-xs leading-relaxed text-slate-600 dark:text-white/65"
          data-testid="roi-assumptions"
        >
          <p>
            We estimate Symplee removes roughly <strong>60% of the hours</strong> a small team
            spends drafting outreach, writing content, and triaging leads — based on
            average customer outcomes across the first 90 days. Your mileage will vary
            depending on workflow complexity and how much knowledge you upload.
          </p>
          <ul className="mt-3 space-y-1.5">
            <li>
              <strong>Hours saved / month</strong> = (hours per week) × 4 weeks × 60%
            </li>
            <li>
              <strong>Labor savings</strong> = hours saved × hourly cost
            </li>
            <li>
              <strong>Net value</strong> = labor savings − ${SYMPLEE_MONTHLY_COST}/mo (FOUNDER19) — never
              less than $0
            </li>
          </ul>
          <p className="mt-3 text-slate-500 dark:text-white/45">
            Not a guarantee — a directional estimate. Talk to us if you want a tailored
            audit (we'll do one free in your first week).
          </p>
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
}

function SliderRow({ label, value, min, max, step, format, onChange, testid }) {
  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <Label className="text-xs uppercase tracking-wider text-slate-500 dark:text-white/55">
          {label}
        </Label>
        <span className="font-display text-xl font-light text-slate-900 dark:text-white" data-testid={`${testid}-value`}>
          {format(value)}
        </span>
      </div>
      <Slider
        value={[value]}
        onValueChange={(arr) => onChange(arr[0])}
        min={min}
        max={max}
        step={step}
        data-testid={testid}
      />
    </div>
  );
}

// Tiny local Label to avoid pulling in a heavier import; matches existing pages.
function Label({ children, className = "" }) {
  return <label className={className}>{children}</label>;
}
